"use client";

import { PreviewCard as PreviewCardPrimitive } from "@base-ui/react/preview-card";
import { TouchDismissLayer } from "./touch-dismiss-layer";
import type React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Box } from "./box";
import { Button } from "./button";
import { Icon, type IconName } from "./icon";
import { Separator } from "./separator";
import { Skeleton, SkeletonGroup } from "./skeleton";

export const PreviewCard: typeof PreviewCardPrimitive.Root =
  PreviewCardPrimitive.Root;

/**
 * Anchor-to-preview gap for every hover-opened preview surface (this popup,
 * the entity chip's preview popover). Three times a pressed popover's 4px:
 * a preview opens while the pointer is still on its anchor, and up close the
 * card reads as stuck to the chip it describes — 8 still did (Spencer,
 * 2026-09-09), 12 clears it.
 */
export const PREVIEW_SIDE_OFFSET = 12;

export function PreviewCardTrigger({
  ...props
}: PreviewCardPrimitive.Trigger.Props): React.ReactElement {
  return (
    <PreviewCardPrimitive.Trigger data-slot="preview-card-trigger" {...props} />
  );
}

export function PreviewCardPopup({
  className,
  children,
  align = "center",
  side,
  sideOffset = PREVIEW_SIDE_OFFSET,
  collisionPadding,
  anchor,
  portalProps,
  dismissCutout,
  ...props
}: PreviewCardPrimitive.Popup.Props & Omittable<{
  /** On a coarse pointer the popup sits over a dismiss layer (see
   *  `TouchDismissLayer`), so the tap that closes it presses nothing beneath.
   *  Name here the one element that must stay tappable through it — the
   *  surface that drives the preview. */
  dismissCutout?: React.ComponentProps<typeof TouchDismissLayer>["cutout"];
  align?: PreviewCardPrimitive.Positioner.Props["align"];
  side?: PreviewCardPrimitive.Positioner.Props["side"];
  sideOffset?: PreviewCardPrimitive.Positioner.Props["sideOffset"];
  /** Minimum gap kept from the viewport edges when the popup is shifted to
   *  fit — a near-viewport-height card names its gutter through this. */
  collisionPadding?: PreviewCardPrimitive.Positioner.Props["collisionPadding"];
  anchor?: PreviewCardPrimitive.Positioner.Props["anchor"];
  portalProps?: PreviewCardPrimitive.Portal.Props;
}>): React.ReactElement {
  return (
    <PreviewCardPrimitive.Portal {...portalProps}>
      {/* Beside the positioner, never inside it: the positioner is
          transformed, which would contain a fixed layer to its own box. */}
      <TouchDismissLayer cutout={dismissCutout} />
      <PreviewCardPrimitive.Positioner
        align={align}
        anchor={anchor}
        className="z-50"
        collisionPadding={collisionPadding}
        data-slot="preview-card-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <PreviewCardPrimitive.Popup
          className={cn(
            "relative flex w-64 origin-(--transform-origin) text-balance rounded-lg border bg-popover not-dark:bg-clip-padding p-4 text-popover-foreground text-sm shadow-popover transition-[scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            className,
          )}
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
          data-slot="preview-card-content"
          {...props}
        >
          {children}
        </PreviewCardPrimitive.Popup>
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  );
}

/**
 * The anatomy every hover preview shares, top to bottom: an identity header
 * the consumer composes, a full-bleed seam, icon-led facts, and one
 * navigation action in the footer. The parts below are the frame, the seam,
 * the facts and the footer; the header is the consumer's, because it shows
 * the consumer's own kinds of thing.
 */
export function PreviewCardFrame({
  children,
  maxWidth = 300,
}: Omittable<{
  children: React.ReactNode;
  /** Bounds the card so long values (an email, a title) truncate; without a
   *  cap the popup grows to fit them. */
  maxWidth?: number;
}>): React.ReactElement {
  return (
    <Box data-slot="preview-card-frame" gap="xs" maxWidth={maxWidth} paddingX="md" paddingY="sm">
      {children}
    </Box>
  );
}

/** A seam across the whole card: cancels the frame's horizontal inset. */
export function PreviewCardSeparator(): React.ReactElement {
  return (
    <Box marginXNegative="md">
      <Separator />
    </Box>
  );
}

/** The facts section: icon-led rows on the 32px pitch a selector list's rows
 *  keep. */
export function PreviewCardFacts({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Box data-slot="preview-card-facts" gap="sm" paddingY="sm">
      {children}
    </Box>
  );
}

/**
 * The facts section while its query is in flight: the SAME box, one 24px row
 * per fact the settled card will show — a 16px glyph stand-in on the 20px
 * rail and a text bar — so the rows hydrate with zero reflow and the card
 * opens at its settled height. Delayed as a group, so an answer from cache
 * paints no placeholder at all.
 */
export function PreviewCardFactsSkeleton({
  rows,
}: {
  rows: number;
}): React.ReactElement {
  return (
    <SkeletonGroup>
      <Box gap="sm" paddingY="sm">
        {Array.from({ length: rows }, (_, index) => (
          <Box align="center" direction="row" gap="sm" height={24} key={index}>
            <Box align="center" direction="row" justify="center" shrink={false} width={20}>
              <Skeleton circle width={16} />
            </Box>
            <Skeleton height={12} vary={index} width={120} />
          </Box>
        ))}
      </Box>
    </SkeletonGroup>
  );
}

/**
 * The card footer: a full-bleed seam, then the one navigation action, padded
 * symmetrically — the frame's own bottom inset is swallowed so the gap above
 * and below the button reads even.
 */
export function PreviewCardFooter({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <Box data-slot="preview-card-footer" marginBottomNegative="sm" marginXNegative="md">
      <Separator />
      <Box direction="column" paddingX="md" paddingY="md">
        <Button onClick={onClick} size="sm" variant="outline">
          <Icon name={icon} size="sm" />
          {label}
        </Button>
      </Box>
    </Box>
  );
}
